'use client';

import {
  useEffect,
  useRef,
  useState,
  useCallback,
  MutableRefObject
} from 'react';
import type Plyr from 'plyr';

export interface PlyrMarker {
  time: number;
  label: string;
}

export interface UsePlyrOptions {
  /** Whether to initialize Plyr (use false to defer initialization) */
  enabled?: boolean;
  /** Markers to display on the progress bar */
  markers?: PlyrMarker[];
  /** Custom controls array. Defaults to standard playback controls */
  controls?: string[];
  /** Enable speed control settings */
  enableSpeedControls?: boolean;
  /** Speed options when enableSpeedControls is true */
  speedOptions?: number[];
  /** Automatically seek to this position (in seconds) when ready */
  autoSeekTo?: number;
  /** Automatically start playback when ready */
  autoPlay?: boolean;
  /** Called when Plyr is ready */
  onReady?: () => void;
  /** Called when metadata is loaded */
  onMetadataLoaded?: () => void;
}

export interface UsePlyrReturn {
  /** Reference to attach to the video element */
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  /** Reference to the Plyr instance */
  plyrRef: MutableRefObject<Plyr | null>;
  /** Whether Plyr is initialized and ready */
  isReady: boolean;
  /** Seek to a specific time in seconds */
  seek: (seconds: number) => void;
  /** Start playback */
  play: () => Promise<void>;
  /** Pause playback */
  pause: () => void;
  /** Get current playback time */
  getCurrentTime: () => number;
}

const DEFAULT_CONTROLS = [
  'play-large',
  'play',
  'progress',
  'current-time',
  'mute',
  'volume',
  'fullscreen',
  'download'
];

const DEFAULT_SPEED_OPTIONS = [1, 1.25, 1.5, 1.75, 2];

/**
 * Hook for managing Plyr video player initialization, controls, and cleanup.
 * Handles dynamic import of Plyr to avoid SSR issues.
 */
export function usePlyr(options: UsePlyrOptions = {}): UsePlyrReturn {
  const {
    enabled = true,
    markers = [],
    controls = DEFAULT_CONTROLS,
    enableSpeedControls = false,
    speedOptions = DEFAULT_SPEED_OPTIONS,
    autoSeekTo,
    autoPlay = false,
    onReady,
    onMetadataLoaded
  } = options;

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const plyrRef = useRef<Plyr | null>(null);
  const [isReady, setIsReady] = useState(false);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasPerformedInitialSeek = useRef(false);

  // Use ref for markers to prevent Plyr re-initialization when markers array changes reference
  // Markers are only used at Plyr init time, so we don't need to re-init when they change
  const markersRef = useRef(markers);
  markersRef.current = markers;

  // Use ref for autoPlay to ensure we have the latest value in the setTimeout callback
  const autoPlayRef = useRef(autoPlay);
  autoPlayRef.current = autoPlay;

  // Use refs for callbacks to avoid re-initializing Plyr when callbacks change
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onMetadataLoadedRef = useRef(onMetadataLoaded);
  onMetadataLoadedRef.current = onMetadataLoaded;

  // Initialize Plyr
  useEffect(() => {
    if (!enabled || !videoRef.current || plyrRef.current) {
      return;
    }

    // Reset state for fresh initialization
    hasPerformedInitialSeek.current = false;

    // Small delay to ensure video element is fully ready
    initTimeoutRef.current = setTimeout(async () => {
      if (!videoRef.current) {
        return;
      }

      try {
        const PlyrModule = await import('plyr');
        const PlyrConstructor = PlyrModule.default;

        const currentMarkers = markersRef.current;
        const plyrOptions: Plyr.Options = {
          controls,
          muted: false,
          volume: 1,
          invertTime: false,
          markers: {
            enabled: currentMarkers.length > 0,
            points: currentMarkers
          }
        };

        if (enableSpeedControls) {
          plyrOptions.settings = ['quality', 'speed', 'loop'];
          plyrOptions.speed = { selected: 1, options: speedOptions };
        } else {
          plyrOptions.settings = [];
        }

        const player = new PlyrConstructor(videoRef.current, plyrOptions);
        plyrRef.current = player;

        // For simple autoPlay (no seeking), play immediately
        // For autoPlay with seeking, wait for metadata to load first
        const needsToSeek = autoSeekTo !== undefined && autoSeekTo > 0;

        if (autoPlay && !needsToSeek) {
          hasPerformedInitialSeek.current = true;
          player.play()?.catch(() => {});
        }

        // Function to perform initial seek AND play (called when metadata is ready)
        const performSeekAndPlay = () => {
          if (!plyrRef.current || hasPerformedInitialSeek.current) {
            return;
          }
          hasPerformedInitialSeek.current = true;

          if (needsToSeek) {
            plyrRef.current.currentTime = autoSeekTo!;
          }

          if (autoPlay) {
            setTimeout(() => {
              plyrRef.current?.play()?.catch(() => {});
            }, 50);
          }
        };

        // Handle ready event
        player.once('ready', () => {
          setIsReady(true);
          onReadyRef.current?.();

          // Check if metadata is already loaded (cached videos)
          if (player.duration > 0) {
            performSeekAndPlay();
          }
        });

        // Handle metadata loaded
        player.once('loadedmetadata', () => {
          onMetadataLoadedRef.current?.();

          // Only perform seek/play if we haven't already
          if (plyrRef.current && plyrRef.current.currentTime === 0) {
            performSeekAndPlay();
          }
        });
      } catch (error) {
        console.error('[usePlyr] Failed to initialize Plyr:', error);
      }
    }, 100);

    // Cleanup
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
      if (plyrRef.current) {
        try {
          plyrRef.current.destroy();
        } catch (err) {
          // Ignore destroy errors
        }
        plyrRef.current = null;
        setIsReady(false);
      }
    };
    // Note: markers, onReady, onMetadataLoaded excluded from deps - we use refs to avoid re-init
  }, [
    enabled,
    controls,
    enableSpeedControls,
    speedOptions,
    autoSeekTo,
    autoPlay
  ]);

  // Control functions
  const seek = useCallback((seconds: number) => {
    if (plyrRef.current) {
      plyrRef.current.currentTime = seconds;
    }
  }, []);

  const play = useCallback(async () => {
    if (plyrRef.current) {
      try {
        await plyrRef.current.play();
      } catch {
        // Autoplay may be blocked by browser
      }
    }
  }, []);

  const pause = useCallback(() => {
    if (plyrRef.current) {
      plyrRef.current.pause();
    }
  }, []);

  const getCurrentTime = useCallback(() => {
    return plyrRef.current?.currentTime ?? 0;
  }, []);

  return {
    videoRef,
    plyrRef,
    isReady,
    seek,
    play,
    pause,
    getCurrentTime
  };
}
